import React, { useState, useRef, useEffect } from "react";

// third-party imports 
import Avatar from "react-avatar";
import Cropper from "react-easy-crop";
import { PiWarningOctagonFill } from "react-icons/pi";
import { FaArrowsAlt } from "react-icons/fa";
import { GoZoomIn, GoZoomOut } from "react-icons/go";

// project imports
import { _setBgColor, getCroppedImage, getOrCreateGuestId } from "../../utils";
import { useUserContext } from "../../context/user_context";
import { showToast } from "../../components/sweet-alert/Sweetalert";
import { MAX_FILE_SIZE } from "../../constants";
import ToolTip from "../../common/Tooltip/ToolTip";

// Reading environment variable start
const userProfileSupportedFiles = process.env.REACT_APP_USER_PROFILE_IMAGE_SUPPORTED_FILES;

export default function UserProfilePicUpload() {

    //Reading state from store
    const { loginData, updateUserProfile } = useUserContext();

    // Reference to the file input DOM element
    const fileInputRef = useRef(null);

    // constant variable 
    const member = loginData && loginData?.data && loginData?.data;
    const userProfileName = member ? member?.userFullName : "Guest User";
    const currentUserId = member ? member?.userRecordId || "" : getOrCreateGuestId();
    const userProfileImg = member ? member?.profileImg?.publicUrl : "";

    // State Initalization
    const [previewUrl, setPreviewUrl] = useState(userProfileImg || null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [initialCroppedAreaPercentages, setInitialCroppedAreaPercentages] = useState(null);
    const [croppedArea, setCroppedArea] = useState(null);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isSaved, setIsSaved] = useState(false);

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    useEffect(() => {
        if (!selectedFile) {
            setPreviewUrl(userProfileImg || null);
        }
    }, [userProfileImg, selectedFile]);

    const handleContainerClick = () => {
        try {
            fileInputRef.current.click();
        } catch (error) {
            console.log("64 : UserProfilePicUpload : Error handleContainerClick", error);
        }
    };

    const handleFileChange = (event) => {
        try {
            const file = event.target.files[0];
            if (!file) return;
            const supportedImage = userProfileSupportedFiles && !userProfileSupportedFiles.split(",").some((type) => {
                const extension = type.trim().toLowerCase();

                return (
                    file.type === extension ||
                    file.name.toLowerCase().endsWith(extension.replace("*", ""))
                );
            });

            if (file.size > MAX_FILE_SIZE) {
                showToast("error", "Image size must be less than 2 MB.", true, "bottom-right", false, true, 5000)
                event.target.value = null;
                return;
            }
            if (supportedImage) {
                showToast("error", "Please select a supported image format.", true, "bottom-right", false, true, 5000);
                event.target.value = "";
                return;
            }

            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
            setIsSaved(false);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedArea(null);
            setCroppedAreaPixels(null);
            setInitialCroppedAreaPercentages(null);
        } catch (error) {
            console.log("101: UserProfilePicUpload : Error handleFilechange", error);
        }
    };

    const onCropComplete = (croppedArea, croppedAreaPixels) => {
        setCroppedArea(croppedArea);
        setCroppedAreaPixels(croppedAreaPixels);
    }

    const handleUpload = async () => {
        if (!selectedFile || !croppedAreaPixels || !previewUrl) {
            return;
        }

        setIsUploading(true);
        try {
            const croppedBlob = await getCroppedImage(
                previewUrl,
                croppedAreaPixels
            );

            const croppedFile = new File(
                [croppedBlob],
                selectedFile.name.replace(/\.[^/.]+$/, "") + ".jpg",
                { type: "image/jpeg" }
            );

            const formData = new FormData();
            formData.append("profileImg", croppedFile);

            await updateUserProfile(currentUserId, formData);

            const croppedPreviewUrl = URL.createObjectURL(croppedBlob);

            setPreviewUrl(croppedPreviewUrl);
            setSelectedFile(null);
            setIsSaved(true);

            if (croppedArea) {
                setInitialCroppedAreaPercentages(croppedArea);
            }

            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } catch (error) {
            console.error("147: UserProfilePicUpload : Error handleUpload", error);

            setSelectedFile(null);
            setPreviewUrl(userProfileImg || null);
        } finally {
            setIsUploading(false);
        }
    };

    const handleCancel = () => {
        try {
            if (isUploading) return;

            setSelectedFile(null);
            // Reset back to original database value (either the old image or null)
            setPreviewUrl(userProfileImg || null);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedArea(null);
            setCroppedAreaPixels(null);
            setIsSaved(false);

            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } catch (error) {
            console.log("173: UserProfilePicUpload : Error handleCancel", error);
        }
    };

    const handleInfo = () => {
        try {
            setShowInfo(!showInfo)
        } catch (error) {
            console.log("181: UserProfilePicUpload : Error handleInfo", error);
        }
    }
    return (
        <div className="d-flex flex-column align-items-center mb-4">

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                // accept="image/*"
                accept={userProfileSupportedFiles}
                className="d-none"
            />
            <div className="top-actions-panel">
                <ToolTip title={"Instructions"} placement="top">
                    <button
                        type="button"
                        className={`action-icon-btn info-toggle-btn ${showInfo ? 'active' : ''}`}
                        onClick={handleInfo}
                    >
                        <PiWarningOctagonFill />
                    </button>
                </ToolTip>
            </div>

            {showInfo && (
                <div className="info-message-box">
                    <p><strong>Profile Image Guidelines:</strong> Ensure your image size is under 2 MB. Only (PNG, JPG, JPEG, HEIC) formats are allowed.</p>
                </div>
            )}

            {!selectedFile && (
                <div
                    className="user-profile position-relative rounded-circle overflow-hidden shadow-lg"
                    onClick={handleContainerClick}
                    title="Click to upload/change photo. For the best fit, use a 200X200 px image."
                >

                    {previewUrl ? (
                        <img
                            src={previewUrl}
                            alt={userProfileName}
                            className="w-100 h-100 object-fit-cover rounded-circle"
                        />
                    ) : (
                        <Avatar
                            size="200"
                            maxInitials={1}
                            color={_setBgColor(userProfileName)}
                            name={userProfileName}
                            textSizeRatio={2.25}
                            round
                            title="Please upload a profile image. For the best fit, use a 200X200 px image."
                        />
                    )}

                    <div
                        className="position-absolute bottom-0 start-0 w-100 bg-dark bg-opacity-50 text-white text-center py-2 small transition-base"
                        title="Click to upload/change photo"
                    >
                        <i className="icon-edit-line me-1"></i> Edit
                    </div>
                </div>
            )}

            {selectedFile && (
                <>
                    {!isSaved && (
                        <>
                            <div className="image-container">
                                <Cropper
                                    key={JSON.stringify(initialCroppedAreaPercentages)}
                                    image={previewUrl}
                                    crop={crop}
                                    zoom={zoom}
                                    zoomSpeed={1}
                                    maxZoom={3}
                                    zoomWithScroll
                                    showGrid
                                    aspect={1}
                                    cropShape="round"
                                    initialCroppedAreaPercentages={initialCroppedAreaPercentages || undefined}
                                    onCropComplete={onCropComplete}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                />
                            </div>
                            <p className="color-body mb-4">
                                <FaArrowsAlt /> Drag Your Photo as required
                            </p>
                            <div className="zoom-control-container">
                                <GoZoomOut className="zoom-icon" />
                                <input
                                    type="range"
                                    value={zoom}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    aria-label="Zoom"
                                    onChange={(e) => setZoom(Number(e.target.value))}
                                    className="zoom-range"
                                />
                                <GoZoomIn className="zoom-icon" />
                            </div>
                        </>
                    )}

                </>
            )}

            {selectedFile && (
                <div className="d-flex gap-2">
                    <button
                        className="edu-btn btn-small btn-border-gray px-3"
                        onClick={handleCancel}
                        disabled={isUploading}
                    >
                        Cancel
                    </button>
                    <button
                        className="edu-btn btn-small btn-primary px-3"
                        onClick={handleUpload}
                        disabled={isUploading}
                    >
                        {isUploading ? "Saving..." : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
}
